import React, { useCallback, useEffect, useMemo, useState } from 'react';
import WhatsAppSendButton from '../../components/WhatsAppSendButton';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { RefreshCw, FileText, Download, X } from 'lucide-react';
import { useSettings } from '../../context/SettingsContext';
import { buildImageUrl } from '../../utils/imageUrl';
import ProjectSelect from '../../components/ProjectSelect';

// Delivery Challan vs Boxes — the Packing Report table (same columns as Packing →
// Export PDF/Word) with each physical box's delivery status:
//   Delivered  = ticked on a challan whose status is "Delivered"
//   On Challan = ticked on a Draft / Sent challan (not delivered yet)
//   Pending    = not ticked on any (non-cancelled) challan
// Each label copy (A, B…) is one physical box. Box data comes from /packing/boxes,
// challan links from /packing/challan-report (same matching as everywhere else).

const STATUS = {
  delivered:  { label: 'Delivered',  cls: 'bg-green-50 text-green-700 border-green-200', color: '#15803d' },
  on_challan: { label: 'On Challan', cls: 'bg-blue-50 text-blue-700 border-blue-200',   color: '#1d4ed8' },
  pending:    { label: 'Pending',    cls: 'bg-gray-100 text-gray-700 border-gray-200',  color: '#4b5563' },
};
const SUB = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const copiesOf = b => Math.max(1, Math.min(26, parseInt(b.copies) || 1));
const thumb = (path, size = 120) => {
  const url = buildImageUrl(path);
  if (!url) return '';
  return url.includes('/image/upload/') ? url.replace('/image/upload/', `/image/upload/w_${size},h_${size},c_fill,q_auto,f_jpg/`) : url;
};
const esc = v => String(v == null || v === '' ? '-' : v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const emptyFilters = { project_id: '', search: '', status: '', challan: '' };

export default function DeliveryBoxesReport({ projects = [] }) {
  const { settings, formatDate } = useSettings();
  const [boxes, setBoxes] = useState([]);
  const [usage, setUsage] = useState(new Map());   // `${box_id}|${label}` → challans[]
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(emptyFilters);
  const set = p => setFilters(f => ({ ...f, ...p }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, r] = await Promise.all([api.get('/packing/boxes'), api.get('/packing/challan-report')]);
      setBoxes(b.data || []);
      const m = new Map();
      (r.data?.boxes || []).forEach(u => m.set(u.unit_key || `${u.id}|${u.box_number}`, u.challans || []));
      setUsage(m);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Groups like the Packing Report: project + item + proto code → physical boxes → content lines
  const groups = useMemo(() => {
    const words = filters.search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const challanQ = filters.challan.trim().toLowerCase();
    const out = []; const gmap = new Map();
    for (const box of boxes) {
      if (filters.project_id && String(box.project_id) !== String(filters.project_id)) continue;
      const proto = box.photo_code || box.item_proto_code || '';
      const itemName = box.main_item || box.item_name || '';
      const hay = `${box.box_number} ${proto} ${itemName} ${box.project_name || ''} ${(box.items || []).map(i => i.item_name).join(' ')}`.toLowerCase();
      if (words.length && !words.every(w => hay.includes(w))) continue;
      const n = copiesOf(box);
      const labels = n > 1 ? Array.from({ length: n }, (_, i) => `${box.box_number}-${SUB[i]}`) : [box.box_number];
      const units = labels.map(dn => {
        const challans = usage.get(`${box.id}|${dn}`) || [];
        const status = challans.some(c => c.challan_status === 'delivered') ? 'delivered' : challans.length ? 'on_challan' : 'pending';
        return { dn, challans, status };
      }).filter(u => (!filters.status || u.status === filters.status)
        && (!challanQ || u.challans.some(c => String(c.challan_number).toLowerCase().includes(challanQ))));
      if (!units.length) continue;
      const key = [box.project_id, itemName, proto].join('|');
      if (!gmap.has(key)) {
        const g = { projectName: box.project_name || box.client_name || '', projCode: box.proj_code || '', itemName, protoCode: proto, boxes: [] };
        gmap.set(key, g); out.push(g);
      }
      const items = box.items?.length ? box.items : [{ item_name: box.item_name || '-', quantity: box.item_quantity ?? '-', unit: box.item_unit || 'PCS' }];
      const img = thumb(box.box_image || box.item_image);
      units.forEach(u => gmap.get(key).boxes.push({ ...u, items, img }));
    }
    return out;
  }, [boxes, usage, filters]);

  const allUnits = groups.flatMap(g => g.boxes);
  const counts = {
    total: allUnits.length,
    delivered: allUnits.filter(u => u.status === 'delivered').length,
    on_challan: allUnits.filter(u => u.status === 'on_challan').length,
    pending: allUnits.filter(u => u.status === 'pending').length,
  };
  const hasFilters = Object.values(filters).some(Boolean);
  const challanText = u => u.challans.map(c => `${c.challan_number} (${formatDate(c.challan_date)}${c.challan_status ? ', ' + c.challan_status : ''})`).join(', ');

  // ── Export (same table as the Packing Report + Status / Challan) ──────────
  const buildHtml = async (forWord) => {
    const urls = [...new Set(allUnits.map(u => u.img).filter(Boolean))];
    const data = new Map();
    const res = await Promise.allSettled(urls.map(async url => {
      const r = await fetch(url); if (!r.ok) throw new Error('fetch');
      const blob = await r.blob();
      return { url, uri: await new Promise((ok, no) => { const fr = new FileReader(); fr.onload = () => ok(fr.result); fr.onerror = no; fr.readAsDataURL(blob); }) };
    }));
    res.forEach(r => { if (r.status === 'fulfilled') data.set(r.value.url, r.value.uri); });
    const imgSrc = u => data.get(u) || u;
    const u_ = forWord ? 'pt' : 'px';
    const W = forWord ? { sno: 24, proj: 112, code: 52, item: 84, proto: 54, boxno: 70, img: 46, desc: 100, qty: 30, uom: 30, st: 50, ch: 80 }
                      : { sno: 34, proj: 150, code: 72, item: 115, proto: 76, boxno: 96, img: 62, desc: 140, qty: 42, uom: 42, st: 70, ch: 120 };
    const th = w => `border:1px solid #999;padding:4px 3px;text-align:center;font-weight:700;font-size:8pt;background:#F2F2F2;width:${w}${u_};`;
    const td = (w, x = '') => `border:1px solid #999;padding:4px 3px;font-size:8pt;vertical-align:middle;width:${w}${u_};${x}`;
    let rows = '', sr = 1;
    groups.forEach(g => {
      const gRows = g.boxes.reduce((s, b) => s + b.items.length, 0);
      let firstG = true;
      g.boxes.forEach(b => {
        let firstB = true;
        b.items.forEach(it => {
          rows += '<tr>';
          if (firstG) {
            rows += `<td rowspan="${gRows}" style="${td(W.sno, 'text-align:center;font-weight:700;')}">${sr}</td>`
              + `<td rowspan="${gRows}" style="${td(W.proj, 'font-weight:700;')}">${esc(g.projectName)}</td>`
              + `<td rowspan="${gRows}" style="${td(W.code)}">${esc(g.projCode)}</td>`
              + `<td rowspan="${gRows}" style="${td(W.item)}">${esc(g.itemName)}</td>`
              + `<td rowspan="${gRows}" style="${td(W.proto, 'font-family:monospace;')}">${esc(g.protoCode)}</td>`;
            firstG = false;
          }
          if (firstB) {
            rows += `<td rowspan="${b.items.length}" style="${td(W.boxno, 'font-weight:700;')}">${esc(b.dn)}</td>`
              + `<td rowspan="${b.items.length}" style="${td(W.img, 'text-align:center;')}">${b.img ? `<img src="${esc(imgSrc(b.img))}" width="40" height="40" style="width:40px;height:40px;object-fit:cover;" />` : '-'}</td>`;
          }
          rows += `<td style="${td(W.desc)}">${esc(it.item_name)}</td><td style="${td(W.qty, 'text-align:right;font-weight:600;')}">${esc(it.quantity)}</td><td style="${td(W.uom)}">${esc(it.unit || 'PCS')}</td>`;
          if (firstB) {
            rows += `<td rowspan="${b.items.length}" style="${td(W.st, `font-weight:700;color:${STATUS[b.status].color};text-align:center;`)}">${STATUS[b.status].label}</td>`
              + `<td rowspan="${b.items.length}" style="${td(W.ch)}">${esc(challanText(b))}</td>`;
            firstB = false;
          }
          rows += '</tr>';
        });
      });
      sr++;
    });
    const now = new Date().toLocaleString('en-IN', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' });
    const head = `<h2 style="font-size:13pt;margin-bottom:4px;">${esc(settings.company_name || 'Workshop')} — Delivery Challan vs Boxes</h2>
      <p style="font-size:8.5pt;color:#555;margin-bottom:10px;">Date: ${esc(now)} &nbsp;|&nbsp; Total Boxes: ${counts.total} &nbsp;|&nbsp; Delivered: ${counts.delivered} &nbsp;|&nbsp; On Challan: ${counts.on_challan} &nbsp;|&nbsp; Pending: ${counts.pending}</p>`;
    const table = `<table style="border-collapse:collapse;width:100%;"><thead><tr>
      <th style="${th(W.sno)}">S.NO</th><th style="${th(W.proj)}">PROJECT NAME</th><th style="${th(W.code)}">PROJECT CODE</th><th style="${th(W.item)}">ITEM NAME</th>
      <th style="${th(W.proto)}">PROTO CODE</th><th style="${th(W.boxno)}">BOX NO</th><th style="${th(W.img)}">IMAGE</th><th style="${th(W.desc)}">ITEM DESCRIPTION</th>
      <th style="${th(W.qty)}">QTY</th><th style="${th(W.uom)}">UOM</th><th style="${th(W.st)}">STATUS</th><th style="${th(W.ch)}">CHALLAN</th>
      </tr></thead><tbody>${rows}</tbody><tfoot><tr>
      <td colspan="5" style="border:1px solid #999;padding:5px;text-align:center;font-weight:700;font-size:8pt;">TOTAL QTY OF BOXES</td>
      <td style="border:1px solid #999;padding:5px;text-align:center;font-weight:700;font-size:8pt;">${counts.total}</td>
      <td colspan="6" style="border:1px solid #999;padding:5px;font-size:8pt;">Delivered ${counts.delivered} · On Challan ${counts.on_challan} · Pending ${counts.pending}</td>
      </tr></tfoot></table>`;
    return forWord
      ? `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40"><head><meta charset="utf-8"/>
         <style>@page{size:A4 landscape;margin:12mm 12mm;mso-page-orientation:landscape;} body{font-family:Arial,sans-serif;}</style></head><body>${head}${table}</body></html>`
      : `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Delivery Challan vs Boxes</title><style>
         *{margin:0;padding:0;box-sizing:border-box} body{font-family:Arial,sans-serif;padding:20px} @media print{@page{size:A4 landscape;margin:10mm 10mm} body{padding:0}}
         </style></head><body>${head}${table}</body></html>`;
  };
  const exportPDF = async () => {
    if (!allUnits.length) return toast.error('No boxes to export');
    const win = window.open('', '_blank');
    if (!win) return toast.error('Allow pop-ups to export');
    win.document.write('<p style="font-family:Arial;padding:20px">Preparing…</p>');
    const html = await buildHtml(false);
    win.document.open(); win.document.write(html); win.document.close(); win.focus();
    setTimeout(() => { try { win.print(); } catch { /* closed */ } }, 600);
  };
  const exportWord = async () => {
    if (!allUnits.length) return toast.error('No boxes to export');
    const html = await buildHtml(true);
    const url = URL.createObjectURL(new Blob(['﻿' + html], { type: 'application/msword;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url; a.download = `Delivery-Challan-vs-Boxes-${new Date().toISOString().slice(0, 10)}.doc`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  const inp = 'border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white';
  let sr = 0;

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-gray-100 p-3 sm:p-4 space-y-3">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          <div className="text-xs text-gray-500 font-semibold">Project
            <div className="mt-1"><ProjectSelect projects={projects} value={filters.project_id} onChange={v => set({ project_id: v })} placeholder="All projects" className={inp + ' w-full'} /></div>
          </div>
          <label className="text-xs text-gray-500 font-semibold">Box / Proto / Item
            <input value={filters.search} onChange={e => set({ search: e.target.value })} placeholder="Box no., code or item" className={inp + ' w-full mt-1'} />
          </label>
          <label className="text-xs text-gray-500 font-semibold">Challan No.
            <input value={filters.challan} onChange={e => set({ challan: e.target.value })} placeholder="e.g. DC-1001" className={inp + ' w-full mt-1'} />
          </label>
          <label className="text-xs text-gray-500 font-semibold">Status
            <select value={filters.status} onChange={e => set({ status: e.target.value })} className={inp + ' w-full mt-1'}>
              <option value="">All</option>
              {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </label>
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          {hasFilters && <button onClick={() => setFilters(emptyFilters)} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 px-2"><X className="w-3.5 h-3.5" /> Clear filters</button>}
          <button onClick={load} disabled={loading} className="flex items-center gap-1.5 border border-gray-200 text-gray-600 hover:bg-gray-50 px-3 py-2 rounded-xl text-sm">
            <RefreshCw className={'w-4 h-4' + (loading ? ' animate-spin' : '')} /> Refresh
          </button>
          <button onClick={exportPDF} className="flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-xl text-sm font-semibold"><FileText className="w-4 h-4" /> Export PDF</button>
          <button onClick={exportWord} className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-xl text-sm font-semibold"><Download className="w-4 h-4" /> Export Word</button>
          <WhatsAppSendButton
            documentType="DELIVERY_CHALLAN_BOXES"
            referenceId={new Date().toISOString().slice(0, 10)}
            fileName={`Delivery Challan vs Boxes ${new Date().toISOString().slice(0, 10)}.pdf`}
            disabled={!allUnits.length}
            getHtml={() => buildHtml(false)}
            className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded-xl text-sm font-semibold disabled:opacity-50"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { l: 'Total Boxes', v: counts.total, c: 'text-purple-700', bg: 'bg-purple-50 border-purple-200' },
          { l: 'Delivered', v: counts.delivered, c: 'text-green-700', bg: 'bg-green-50 border-green-200' },
          { l: 'On Challan (not delivered)', v: counts.on_challan, c: 'text-blue-700', bg: 'bg-blue-50 border-blue-200' },
          { l: 'Pending', v: counts.pending, c: 'text-amber-700', bg: 'bg-amber-50 border-amber-200' },
        ].map(x => (
          <div key={x.l} className={`${x.bg} border rounded-2xl p-3 text-center`}>
            <p className={`text-xl font-bold ${x.c}`}>{x.v}</p>
            <p className={`text-xs font-semibold mt-0.5 ${x.c}`}>{x.l}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        {loading && !boxes.length ? (
          <div className="flex justify-center py-12"><div className="animate-spin w-7 h-7 border-4 border-purple-500 border-t-transparent rounded-full" /></div>
        ) : !groups.length ? (
          <div className="text-center py-12 text-gray-400 text-sm">No boxes match these filters</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead className="bg-gray-50">
                <tr className="text-[11px] font-semibold text-gray-600 uppercase">
                  {['S.No', 'Project Name', 'Project Code', 'Item Name', 'Proto Code', 'Box No', 'Image', 'Item Description', 'Qty', 'UOM', 'Status', 'Challan'].map(h =>
                    <th key={h} className="border border-gray-200 px-2 py-2 text-center whitespace-nowrap">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {groups.map((g, gi) => {
                  sr += 1;
                  const gRows = g.boxes.reduce((s, b) => s + b.items.length, 0);
                  return g.boxes.map((b, bi) => b.items.map((it, ii) => (
                    <tr key={`${gi}-${bi}-${ii}`} className="align-middle">
                      {bi === 0 && ii === 0 && <>
                        <td rowSpan={gRows} className="border border-gray-200 px-2 py-1.5 text-center font-bold">{sr}</td>
                        <td rowSpan={gRows} className="border border-gray-200 px-2 py-1.5 font-semibold">{g.projectName || '—'}</td>
                        <td rowSpan={gRows} className="border border-gray-200 px-2 py-1.5">{g.projCode || '—'}</td>
                        <td rowSpan={gRows} className="border border-gray-200 px-2 py-1.5">{g.itemName || '—'}</td>
                        <td rowSpan={gRows} className="border border-gray-200 px-2 py-1.5 font-mono">{g.protoCode || '—'}</td>
                      </>}
                      {ii === 0 && <>
                        <td rowSpan={b.items.length} className="border border-gray-200 px-2 py-1.5 font-bold whitespace-nowrap">{b.dn}</td>
                        <td rowSpan={b.items.length} className="border border-gray-200 px-1 py-1 text-center">
                          {b.img ? <img src={b.img} alt="" className="w-10 h-10 object-cover rounded mx-auto" /> : '—'}
                        </td>
                      </>}
                      <td className="border border-gray-200 px-2 py-1.5">{it.item_name}</td>
                      <td className="border border-gray-200 px-2 py-1.5 text-right font-semibold">{it.quantity}</td>
                      <td className="border border-gray-200 px-2 py-1.5">{it.unit || 'PCS'}</td>
                      {ii === 0 && <>
                        <td rowSpan={b.items.length} className="border border-gray-200 px-2 py-1.5 text-center">
                          <span className={`inline-block whitespace-nowrap text-[11px] font-semibold px-2 py-0.5 rounded-full border ${STATUS[b.status].cls}`}>{STATUS[b.status].label}</span>
                        </td>
                        <td rowSpan={b.items.length} className="border border-gray-200 px-2 py-1.5">
                          {b.challans.length ? b.challans.map(c => (
                            <div key={c.id} className="min-w-[8rem]"><span className="font-semibold text-teal-700 whitespace-nowrap">{c.challan_number}</span>
                              <span className="text-gray-400"> {formatDate(c.challan_date)}{c.challan_status ? ` · ${c.challan_status}` : ''}</span></div>
                          )) : <span className="text-gray-400">—</span>}
                        </td>
                      </>}
                    </tr>
                  )));
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
